import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tabs,
  Tab,
  TextField,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DataLandScape } from './EnterpriseContextInterfaces';
import AddPlatformModal from './AddPlatformModal';
import Modal from '../../common/components/Modal/Modal';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DataLandscapeRow from './DataLandscapeRow';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { IOrganization } from '../../common/types/organisation';
import { getIndustryTechnologyComplete } from '../../services/commonService';
import { getOfficeRank } from './utility/officeMapping';
import Loader from '../../common/components/Loader';
import EnterpriseLicenseHeader from './softwareDetailsUploader/UploadLicenseHeader';
import EnterpriseUploadModal from './softwareDetailsUploader/EnterpriseUploadModal';
import { ENVIRONMENT } from '../../config/env';
import {
  getLicenseUploadStatus,
  saveKnowledgeBase,
  updateTechLandscape,
  uploadEvaluatedData,
} from './softwareDetailsUploader/LicenseService';
import {
  EnterpriseContextItem,
  LicenseApiFile,
  TechLandscapeUpdate,
  UploadedFile,
} from './softwareDetailsUploader/uploaderTypes';

interface DataLandscapeFormProps {
  enterpriseContext?: DataLandScape[];
  onAddPlatform?: (platformData: DataLandScape) => void;
  onDeletePlatform?: (index: number) => void;
  onSave?: () => void;
  onTechnologyDataChange?: (data: any) => void;
  setIsDirty?: (data: boolean) => void;
  genAIStack: string[];
  genAIOthers: string[];
  dataLake: string[];
  cloudProvider: string[];
  setCloudProvider: (data: string[]) => void;
  setGenAIStack: (data: string[]) => void;
  setGenAIOthers: (data: string[]) => void;
  setDataLake: (data: string[]) => void;
  setIsSaveModalOpen?: (data: boolean) => void;
  technologyLandscapeData: any;
}

// API Response Interfaces
interface Technology {
  id: string;
  name: string;
  type: 'Core' | 'Other' | 'SAP Previous Versions' | 'Oracle Previous Versions';
}

interface FunctionData {
  id: string;
  name: string;
  office?: string;
  technologies: Technology[];
}

interface IndustryData {
  id: string;
  name: string;
  functions: FunctionData[];
}

const DataLandscapeForm: React.FC<DataLandscapeFormProps> = ({
  onAddPlatform,
  onTechnologyDataChange,
  setIsDirty,
  genAIStack,
  genAIOthers,
  dataLake,
  setGenAIStack,
  setGenAIOthers,
  setDataLake,
  cloudProvider,
  setCloudProvider,
  technologyLandscapeData,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const companyId = localStorage.getItem('current_organization') || '';
  const organisation = useSelector((state: RootState) => state.organisation);
  const enterpriseData = useSelector((state: RootState) => state.enterpriseContext);
  const selectedOrg = organisation.find((org: IOrganization) => org.id === companyId);

  // State for API data
  const [functions, setFunctions] = useState<FunctionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [platformData, setPlatformData] = useState<DataLandScape[]>([]);
  const [industryData, setIndustryData] = useState<IndustryData | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [preLoadedLicenseFiles, setPreLoadedLicenseFiles] = useState<LicenseApiFile[]>([]);
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [serverResultId, setServerResultId] = useState<string>('');
  const [manageMode, setManageMode] = useState(false);
  const [updatingTechLandscape, setUpdatingTechLandscape] = useState<boolean>(false);
  const [autoFilledRows, setAutoFilledRows] = useState<Set<number>>(new Set());
  const [files, setFiles] = useState<UploadedFile[]>([]);

  // Ref to track if we're in initialization phase
  const isInitializingRef = useRef(true);

  // Infrastructure state
  // const [cloudProviderOthers, setCloudProviderOthers] = useState<string>('');
  // const [dataLakeOthers, setDataLakeOthers] = useState<string>('');

  // Infrastructure options
  const cloudProviderOptions = ['AWS', 'Microsoft', 'Google'];

  const genAIStackOptions = [
    'Microsoft Copilot Studio',
    'AWS AI Stack',
    'Google AI Vertex',
    'Azure AI Foundry',
    'Palantir',
    'Amazon Bedrock',
    'LangChain – Custom',
    'LlamaIndex',
    // 'Others'
  ];

  const dataLakeOptions = [
    'Databricks',
    'Snowflake',
    'Microsoft Fabric (OneLake)',
    // 'Others'
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleInfrastructureChange = (field: string, value: string | string[]) => {
    switch (field) {
      case 'cloudProvider': {
        const selected: string[] = Array.isArray(value) ? value : [value];
        setCloudProvider(selected);
        setIsDirty?.(true);
        break;
      }

      case 'dataLake': {
        const selected: string[] = Array.isArray(value) ? value : [value];
        setDataLake(selected);
        setIsDirty?.(true);
        break;
      }

      case 'genAIStack': {
        const selected = Array.isArray(value) ? value : [value];
        setGenAIStack(selected);
        if (!selected.includes('Others')) {
          setGenAIOthers(['']);
        }
        setIsDirty?.(true);
        break;
      }

      case 'genAIOthers':
        setGenAIOthers(value as string[]);
        setIsDirty?.(true);
        break;
    }
  };

  // Function to map redux technology_landscape back to platformData format
  const mapTechnologyLandscapeToPlatformData = useCallback(
    (techLandscape: any, functionsData: FunctionData[]) => {
      if (
        !techLandscape ||
        !techLandscape.industry ||
        !techLandscape.industry.functions ||
        !Array.isArray(techLandscape.industry.functions)
      ) {
        return [];
      }

      return functionsData
        .map((functionData) => {
          const savedFunction = techLandscape.industry.functions.find(
            (sf: any) => sf.id === functionData.id
          );

          // Extract selected technologies if found in saved data
          let coreModuleProduct = '';
          let otherSoftware = '';
          const otherTechnologyVendors: string[] = [];

          if (savedFunction) {
            savedFunction.technologies?.forEach((tech: any) => {
              const matchingTech = functionData.technologies.find((t) => t.id === tech.id);
              if (matchingTech) {
                if (tech.type === 'Core') {
                  coreModuleProduct = tech.name;
                } else if (tech.type === 'Other') {
                  otherSoftware = tech.name;
                }
              } else if (tech.id?.startsWith('custom_')) {
                // Custom technology vendor
                otherTechnologyVendors.push(tech.name);
              }
            });
          }

          return {
            name: functionData.name,
            description: '',
            default: false,
            applicable: true,
            dataSource: false,
            embeddedAI: false,
            tech_vendor: [],
            vendors: [
              {
                vendor: '',
                version: '',
                notes: '',
                functions: [],
              },
            ],
            dataDescription: '',
            function: functionData.id,
            coreModuleProduct,
            otherSoftware,
            otherTechnologyVendors,
          };
        })
        .filter(Boolean);
    },
    []
  );

  // Function to prepare API payload according to curl format
  const prepareAPIPayload = useCallback(() => {
    if (!industryData || !selectedOrg) return null;

    const functionsPayload = platformData
      .map((platform) => {
        const functionData = functions.find((f) => f.id === platform.function);
        if (!functionData) return null;

        const selectedTechnologies: Technology[] = [];

        // Add core module product if selected
        if (platform.coreModuleProduct) {
          const coreTech = functionData.technologies.find(
            (tech) => tech.name === platform.coreModuleProduct && tech.type === 'Core'
          );
          if (coreTech) selectedTechnologies.push(coreTech);
        }

        // Add other software if selected
        if (platform.otherSoftware) {
          const otherTech = functionData.technologies.find(
            (tech) => tech.name === platform.otherSoftware && tech.type === 'Other'
          );
          if (otherTech) selectedTechnologies.push(otherTech);
        }

        // Add other technology vendors
        if (platform.otherTechnologyVendors && platform.otherTechnologyVendors.length > 0) {
          platform.otherTechnologyVendors.forEach((vendor) => {
            selectedTechnologies.push({
              id: `custom_${Date.now()}_${vendor}`,
              name: vendor,
              type: 'Other',
            });
          });
        }

        return {
          id: functionData.id,
          name: functionData.name,
          technologies: selectedTechnologies,
        };
      })
      .filter(Boolean);

    // Format according to curl request structure
    return {
      industry: {
        name: industryData.name,
        id: industryData.id,
        functions: functionsPayload,
      },
    };
  }, [industryData, selectedOrg, platformData, functions]);

  const fetchTechData = async () => {
    try {
      setLoading(true);
      const industryId = enterpriseData?.enterpriseContext?.industry?.[0] || '';
      const result = await getIndustryTechnologyComplete(industryId);

      let fetchedIndustryData: IndustryData | null = null;

      if (result && typeof result === 'object') {
        if (result.functions && Array.isArray(result.functions)) {
          fetchedIndustryData = result as unknown as IndustryData;
        } else if (result.data && result.data.functions && Array.isArray(result.data.functions)) {
          fetchedIndustryData = result.data as IndustryData;
        }
      }

      if (fetchedIndustryData && fetchedIndustryData.functions) {
        // Sort functions by office type then by name using utility
        fetchedIndustryData.functions.sort((a, b) => {
          const rankA = getOfficeRank(a.name);
          const rankB = getOfficeRank(b.name);
          if (rankA !== rankB) return rankA - rankB;
          return a.name.localeCompare(b.name);
        });
      }

      if (
        fetchedIndustryData &&
        fetchedIndustryData.functions &&
        Array.isArray(fetchedIndustryData.functions)
      ) {
        setFunctions(fetchedIndustryData.functions);
        setIndustryData(fetchedIndustryData);

        // Check if we have saved technology_landscape data in redux
        const savedTechLandscape = enterpriseData?.enterpriseContext?.technology_landscape;

        if (savedTechLandscape && Object.keys(savedTechLandscape).length > 0) {
          // Map saved data back to platform format
          const restoredPlatforms = mapTechnologyLandscapeToPlatformData(
            savedTechLandscape,
            fetchedIndustryData.functions
          );
          if (restoredPlatforms.length > 0) {
            setPlatformData(restoredPlatforms);
          } else {
            // Fallback to creating initial platforms if mapping fails
            const initialPlatforms = fetchedIndustryData.functions.map((func) => ({
              name: func.name,
              description: '',
              default: false,
              applicable: true,
              dataSource: false,
              embeddedAI: false,
              tech_vendor: [],
              vendors: [
                {
                  vendor: '',
                  version: '',
                  notes: '',
                  functions: [],
                },
              ],
              dataDescription: '',
              function: func.id,
              coreModuleProduct: '',
              otherSoftware: '',
              otherTechnologyVendors: [],
            }));
            setPlatformData(initialPlatforms);
          }
        } else {
          // Create initial platform data if no saved data
          const initialPlatforms = fetchedIndustryData.functions.map((func) => ({
            name: func.name,
            description: '',
            default: false,
            applicable: true,
            dataSource: false,
            embeddedAI: false,
            tech_vendor: [],
            vendors: [
              {
                vendor: '',
                version: '',
                notes: '',
                functions: [],
              },
            ],
            dataDescription: '',
            function: func.id,
            coreModuleProduct: '',
            otherSoftware: '',
            otherTechnologyVendors: [],
          }));
          setPlatformData(initialPlatforms);
        }
      } else {
        console.error('Invalid API response structure:', result);
      }
    } catch (err) {
      console.error('Error fetching technology data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTechLandscapeUpdate = async (filesData: TechLandscapeUpdate): Promise<void> => {
    try {
      setUpdatingTechLandscape(true);

      const fileUploadPayload = filesData?.files?.map((file: any) => {
        return {
          file_name: file?.name,
          file_content: file?.fileData,
          description: file?.summary,
          is_valid: file?.isValid,
        };
      });
      const fileUploadResponse = await uploadEvaluatedData({ items: fileUploadPayload });

      if (fileUploadResponse) {
        const files = filesData?.files?.map((file: any) => {
          return {
            file_name: file?.name,
            is_valid: file?.isValid,
            validation_status: file?.status,
            one_line_summary: file?.summary,
            fileData: file?.fileData,
            fileUrl: file?.fileUrl,
            id: file?.id,
            env: ENVIRONMENT,
          };
        });

        const payload = {
          files: files,
          'enterprise-technology': {
            organization_id: industryData?.id,
            company_name: industryData?.name,
            description: '', //Need to do this
            industry_data: functions,
            technology_infra: technologyLandscapeData?.technology_infra,
            selected_techlandscape_data: technologyLandscapeData,
          },
          ...(knowledgeBase?.length > 0 && {
            current_landscape: knowledgeBase,
          }),
        };

        console.log('payload --> ', payload);

        const techLandscapeResponse = await updateTechLandscape(payload);

        console.log(
          'techLandscapeResponse --> ',
          techLandscapeResponse?.enterprise_context_payload
        );

        if (techLandscapeResponse?.enterprise_context_payload) {
          const responseData: EnterpriseContextItem[] =
            techLandscapeResponse.enterprise_context_payload;
          const responseMap = new Map<string, EnterpriseContextItem>(
            responseData.map((item) => [item.id, item])
          );

          // Track which rows were auto-filled
          const filledIndices = new Set<number>();

          setPlatformData((prev) =>
            prev.map((prevFunc: any, idx: number) => {
              const matched = responseMap.get(prevFunc.function);
              if (!matched) return prevFunc;

              // Mark this row as auto-filled if any field has data
              if (
                matched.core?.name ||
                matched.otherSoftware?.name ||
                matched.otherTechnologyVendors
              ) {
                filledIndices.add(idx);
              }

              return {
                ...prevFunc,
                coreModuleProduct: matched.core?.name,
                otherSoftware: matched.otherSoftware?.name,
                otherTechnologyVendors:
                  matched.otherTechnologyVendors?.split(',').map((v: string) => v.trim()) ?? [],
              };
            })
          );

          // Update the auto-filled rows tracking
          setAutoFilledRows(filledIndices);
        }

        let knowledBaseResponse = null;
        if (techLandscapeResponse?.knowledge_base) {
          const payload = {
            knowledge_base: techLandscapeResponse?.knowledge_base,
            enterprise_context_payload: techLandscapeResponse?.enterprise_context_payload,
          };
          knowledBaseResponse = await saveKnowledgeBase(payload);
        }

        if (techLandscapeResponse && knowledBaseResponse) {
          getUploadedLicenseDetails();
          setFiles([]);
          setUploadModalOpen(false);
          setManageMode(false);
        }
      }
    } catch (err) {
      console.error(err);
      setUpdatingTechLandscape(false);
    } finally {
      setUpdatingTechLandscape(false);
    }
  };

  const getUploadedLicenseDetails = async () => {
    const response = await getLicenseUploadStatus();

    if (!response) return;

    const files = response.result?.files ?? [];
    setKnowledgeBase(response.result?.knowledge_base);
    setPreLoadedLicenseFiles(files);
    setServerResultId(response.result?.id ?? '');
  };

  useEffect(() => {
    getUploadedLicenseDetails();
  }, []);

  // Only notify parent when platformData changes (not on every render)
  useEffect(() => {
    if (!industryData || !selectedOrg || platformData.length === 0) return;

    const payload = prepareAPIPayload();
    // Skip callback during initialization, only call it when user makes actual changes
    if (payload && onTechnologyDataChange && !isInitializingRef.current) {
      onTechnologyDataChange(payload);
    }

    // Mark initialization as complete after first render
    isInitializingRef.current = false;
  }, [platformData]); // Remove prepareAPIPayload dependency to avoid infinite loops

  useEffect(() => {
    if (enterpriseData?.enterpriseContext?.industry?.length > 0) {
      // Reset initialization flag when industry changes
      isInitializingRef.current = true;
      fetchTechData();
    }
  }, [enterpriseData?.enterpriseContext?.industry]);

  // Helper function to get technologies for a specific function
  const getTechnologiesForFunction = (functionId: string) => {
    const selectedFunction = functions.find((func) => func.id === functionId);
    return selectedFunction ? selectedFunction.technologies : [];
  };

  // Helper function to get core products for a specific function
  const getCoreProductsForFunction = (functionId: string) => {
    const technologies = getTechnologiesForFunction(functionId);
    return technologies
      .filter((tech) => tech.type === 'Core')
      .map((tech) => tech.name)
      .sort();
  };

  // Helper function to get other software for a specific function
  const getOtherSoftwareForFunction = (functionId: string) => {
    const technologies = getTechnologiesForFunction(functionId);
    return technologies
      .filter((tech) => tech.type === 'Other')
      .map((tech) => tech.name)
      .sort();
  };

  const handleFieldChange = (index: number, field: string, value: string | string[]) => {
    setPlatformData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // If function is changed, reset the product selections
      if (field === 'function') {
        updated[index].coreModuleProduct = '';
        updated[index].otherSoftware = '';
        updated[index].otherTechnologyVendors = [];
      }
      console.log('field change');
      setIsDirty?.(true);
      return updated;
    });
  };

  const handleModalSave = (platformData: { name: string; description: string }) => {
    const newPlatform: DataLandScape = {
      name: platformData.name,
      description: platformData.description,
      default: false,
      applicable: true,
      dataSource: false,
      embeddedAI: false,
      tech_vendor: [],
      vendors: [
        {
          vendor: '',
          version: '',
          notes: '',
          functions: [],
        },
      ],
      dataDescription: '',
      function: '',
      coreModuleProduct: '',
      otherSoftware: '',
      otherTechnologyVendors: [],
    };

    if (onAddPlatform) {
      onAddPlatform(newPlatform);
    }

    setIsModalOpen(false);
  };

  const handleDeleteClick = (index: number) => {
    setDeleteIndex(index);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex !== null) {
      // Reset the row to empty state instead of deleting
      setPlatformData((prev) => {
        const updated = [...prev];
        const functionId = updated[deleteIndex].function; // Keep the function ID
        const functionName = functions.find((f) => f.id === functionId)?.name || '';

        // Reset the row to initial empty state
        updated[deleteIndex] = {
          name: functionName,
          description: '',
          default: false,
          applicable: true,
          dataSource: false,
          embeddedAI: false,
          tech_vendor: [],
          vendors: [
            {
              vendor: '',
              version: '',
              notes: '',
              functions: [],
            },
          ],
          dataDescription: '',
          function: functionId,
          coreModuleProduct: '',
          otherSoftware: '',
          otherTechnologyVendors: [],
        };

        return updated;
      });
      setIsDirty?.(true);
    }
    setDeleteConfirmOpen(false);
    setDeleteIndex(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDeleteIndex(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '750px',
        }}
      >
        <Loader message="Loading Technology Landscape data" />
      </Box>
    );
  }

  // Render Technology Usage by Function Tab Content
  const renderTechnologyUsageTab = () => (
    <Box>
      {/* Table Container */}
      <EnterpriseLicenseHeader
        onUploadClick={() => {
          setManageMode(preLoadedLicenseFiles.length > 0); // Manage if files exist, Upload otherwise
          setUploadModalOpen(true);
        }}
        hasPreloadedFiles={preLoadedLicenseFiles?.length > 0 || false}
      />
      <Box sx={{ mb: 4, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: '#2c3e50',
                  fontSize: '0.95rem',
                  py: 2,
                  minWidth: 140,
                  border: 'none',
                  borderBottom: '1px solid #e9ecef',
                }}
              >
                Function
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: '#2c3e50',
                  fontSize: '0.95rem',
                  py: 2,
                  minWidth: 250,
                  border: 'none',
                  borderBottom: '1px solid #e9ecef',
                }}
              >
                Core Module Product Name
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: '#2c3e50',
                  fontSize: '0.95rem',
                  py: 2,
                  minWidth: 160,
                  border: 'none',
                  borderBottom: '1px solid #e9ecef',
                }}
              >
                Other Software
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: '#2c3e50',
                  fontSize: '0.95rem',
                  py: 2,
                  minWidth: 200,
                  border: 'none',
                  borderBottom: '1px solid #e9ecef',
                }}
              >
                Other Technology Vendors
              </TableCell>
              <TableCell
                sx={{
                  width: 60,
                  border: 'none',
                  borderBottom: '1px solid #e9ecef',
                }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {platformData.map((platform, index) => (
              <DataLandscapeRow
                key={`platform-${index}-${platform.function}`}
                platform={platform}
                index={index}
                functions={functions}
                coreModuleProducts={getCoreProductsForFunction(platform.function ?? '')}
                otherSoftwareOptions={getOtherSoftwareForFunction(platform.function ?? '')}
                handleFieldChange={handleFieldChange}
                handleDeleteClick={handleDeleteClick}
                isAutoFilled={autoFilledRows.has(index)}
              />
            ))}
          </TableBody>
        </Table>
        <EnterpriseUploadModal
          isOpen={uploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
            setManageMode(false);
          }}
          onUpdate={handleTechLandscapeUpdate}
          preLoadedFiles={preLoadedLicenseFiles}
          openInManageMode={manageMode}
          serverResultId={serverResultId}
          getUploadedLicenseDetails={getUploadedLicenseDetails}
          updatingTechLandscape={updatingTechLandscape}
          files={files}
          setFiles={setFiles}
        />
      </Box>
    </Box>
  );

  // Render Technology Infrastructure Tab Content
  const renderTechnologyInfrastructureTab = () => (
    <Box sx={{ mt: 3, overflowX: 'auto' }}>
      <Table sx={{ minWidth: 500, width: '100%' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
            <TableCell
              sx={{
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                color: '#2C2E50',
                fontSize: '16px',
                py: 2,
                minWidth: 200,
                border: 'none',
                borderBottom: '1px solid #e9ecef',
                width: '20%',
              }}
            >
              Infrastructure
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                color: '#2C2E50',
                fontSize: '16px',
                py: 2,
                minWidth: 300,
                border: 'none',
                borderBottom: '1px solid #e9ecef',
              }}
            >
              Provider
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Cloud Provider Row */}
          <TableRow sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
            <TableCell
              sx={{
                py: 2,
                fontSize: '16px',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 500,
                color: '#000000',
                border: 'none',
                // borderBottom: '1px solid #e9ecef'
              }}
            >
              Cloud Provider
            </TableCell>
            <TableCell
              sx={{
                py: 1.5,
                border: 'none',
                borderBottom: '1px solid #e9ecef',
              }}
            >
              <Autocomplete
                key={`cloud-provider-${cloudProvider}`}
                multiple
                freeSolo
                limitTags={5}
                options={cloudProviderOptions}
                value={cloudProvider}
                onChange={(_event, newValue) => {
                  setCloudProvider(newValue);
                  setIsDirty?.(true);
                }}
                renderTags={(value: string[], getTagProps) =>
                  value?.map((option: string, index: number) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      deleteIcon={
                        <CloseIcon
                          sx={{
                            fontSize: 12,
                            color: 'primary.main',
                            transition: 'color 0.2s',
                          }}
                        />
                      }
                      sx={{
                        backgroundColor: '#f0f4ff',
                        borderRadius: '6px',
                        border: '1px solid',
                        borderColor: '#1A55F9',
                        color: 'primary.main',
                        fontWeight: 400,
                        fontSize: 14,
                        height: 28,
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select or type Cloud Provider"
                  />
                )}
                sx={{
                  backgroundColor: 'white',
                  minWidth: 250,
                  '& .MuiOutlinedInput-root': { paddingY: '4px' },
                }}
              />

              {/* {cloudProvider === 'Others' && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Please specify other Cloud Provider..."
                  value={cloudProviderOthers}
                  onChange={(e) => handleInfrastructureChange('cloudProviderOthers', e.target.value)}
                  sx={{ minWidth: 250 }}
                />
              )} */}
            </TableCell>
          </TableRow>

          {/* GenAI Stack Row */}
          <TableRow sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
            <TableCell
              sx={{
                py: 2,
                fontSize: '16px',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 500,
                color: '#000000',
                border: 'none',
                // borderBottom: '1px solid #e9ecef'
              }}
            >
              GenAI Stack
            </TableCell>
            <TableCell
              sx={{
                py: 1.5,
                border: 'none',
                borderBottom: '1px solid #e9ecef',
              }}
            >
              <Box>
                <Autocomplete
                  key={`genai-stack-${genAIStack.join('-')}`}
                  multiple
                  freeSolo
                  options={genAIStackOptions}
                  value={genAIStack}
                  onChange={(_event, newValue) =>
                    handleInfrastructureChange('genAIStack', newValue)
                  }
                  renderTags={(value: string[], getTagProps) =>
                    value?.map((option: string, index: number) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        deleteIcon={
                          <CloseIcon
                            sx={{
                              fontSize: 12,
                              color: 'primary.main',
                              transition: 'color 0.2s',
                            }}
                          />
                        }
                        sx={{
                          backgroundColor: '#f0f4ff',
                          borderRadius: '6px',
                          border: '1px solid',
                          borderColor: '#1A55F9',
                          color: 'primary.main',
                          fontWeight: 400,
                          fontSize: 14,
                          height: 28,
                          '& .MuiChip-deleteIcon': {
                            marginRight: '4px',
                            color: 'primary.main',
                            fontSize: 14,
                          },
                          '& .MuiChip-deleteIcon:hover': {
                            color: 'error.main',
                            backgroundColor: 'transparent',
                          },
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select GenAI Stack options"
                      sx={{
                        '& .MuiInputBase-input::placeholder': {
                          color: '#8d9499',
                          opacity: 1,
                        },
                        fontSize: 16,
                        fontFamily: 'Poppins, sans-serif',
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: '#1A55F9',
                            borderWidth: 1,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e0e0e0',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1A55F9',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1A55F9',
                            borderWidth: 1,
                          },
                        },
                      }}
                    />
                  )}
                  sx={{
                    backgroundColor: 'white',
                    minWidth: 250,
                    mb: genAIStack.includes('Others') ? 2 : 0,
                    '& .MuiOutlinedInput-root': {
                      paddingY: '4px',
                    },
                  }}
                />

                {genAIStack.includes('Others') && (
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Please specify other GenAI Stack..."
                    value={genAIOthers}
                    onChange={(e) => handleInfrastructureChange('genAIOthers', e.target.value)}
                    sx={{
                      minWidth: 250,
                      '& .MuiInputBase-input::placeholder': {
                        color: '#8d9499',
                        opacity: 1,
                      },
                      fontSize: 16,
                      fontFamily: 'Poppins, sans-serif',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#1A55F9',
                          borderWidth: 1,
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#e0e0e0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1A55F9',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1A55F9',
                          borderWidth: 1,
                        },
                      },
                    }}
                  />
                )}
              </Box>
            </TableCell>
          </TableRow>

          {/* Data Lake Row */}
          <TableRow sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
            <TableCell
              sx={{
                py: 2,
                fontSize: '16px',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 500,
                color: '#000000',
                border: 'none',
                // borderBottom: '1px solid #e9ecef'
              }}
            >
              Data Lake
            </TableCell>
            <TableCell
              sx={{
                py: 1.5,
                border: 'none',
                borderBottom: '1px solid #e9ecef',
              }}
            >
              <Box>
                <Autocomplete
                  key={`data-lake-${dataLake}`}
                  multiple
                  freeSolo
                  limitTags={3}
                  options={dataLakeOptions}
                  value={dataLake}
                  onChange={(_event, newValue) => {
                    setDataLake(newValue);
                    setIsDirty?.(true);
                  }}
                  renderTags={(value: string[], getTagProps) =>
                    value?.map((option: string, index: number) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        deleteIcon={<CloseIcon sx={{ fontSize: 12, color: 'primary.main' }} />}
                        sx={{
                          backgroundColor: '#f0f4ff',
                          borderRadius: '6px',
                          border: '1px solid',
                          borderColor: '#1A55F9',
                          color: 'primary.main',
                          fontWeight: 400,
                          fontSize: 14,
                          height: 28,
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Select or type Data Lake"
                    />
                  )}
                  sx={{
                    backgroundColor: 'white',
                    minWidth: 250,
                    '& .MuiOutlinedInput-root': { paddingY: '4px' },
                  }}
                />

                {/* {dataLake === 'Others' && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Please specify other Data Lake..."
                  value={dataLakeOthers}
                  onChange={(e) => handleInfrastructureChange('dataLakeOthers', e.target.value)}
                  sx={{ minWidth: 250 }}
                />
              )}   */}
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          sx={{ fontWeight: 600, color: '#333', pb: 0.5 }}
        >
          {selectedOrg?.name || ''}
        </Typography>

        {industryData?.name && (
          <Chip
            label={industryData?.name}
            variant="outlined"
            size="small"
            sx={{
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: 12,
              color: '#4D5358',
              borderColor: '#DDE1E6',
              backgroundColor: '#F9FAFB',
            }}
          />
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, overflowX: 'auto' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontFamily: 'Poppins, sans-serif',
              fontSize: { xs: '16px', md: '18px' },
              fontWeight: 600,
              color: '#000000',
              minHeight: 48,
              p: { xs: 1, md: 2 },
            },
            '& .Mui-selected': {
              color: '#1A55F9 !important',
              fontWeight: 500,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1A55F9',
              height: 3,
            },
          }}
        >
          <Tab label="Enterprise Applications" />
          <Tab label="Technology Infrastructure" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && renderTechnologyUsageTab()}
      {activeTab === 1 && renderTechnologyInfrastructureTab()}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        title="Delete Confirmation"
        maxWidth="md"
        height="auto"
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
            <IconButton>
              <WarningAmberIcon fontSize="medium" />
            </IconButton>
            <Typography>This will delete the technology landscape permanently.</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'end', marginBottom: '10px' }}>
            <Button
              onClick={handleDeleteCancel}
              color="primary"
              sx={{ backgroundColor: '#ffffff', marginRight: '10px' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDeleteConfirm}
              color="primary"
              sx={{
                textTransform: 'none',
                minWidth: 80,
                fontWeight: 400,
                fontSize: 14,
                color: '#FFFFFF',
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Add Platform Modal */}
      <AddPlatformModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleModalSave}
      />
    </Box>
  );
};

export default DataLandscapeForm;
